import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { WidenService } from '@services/widen.service';
import {
  ArticleHubContent,
  DocumentRef,
  RelatedStrategies,
  StrategiesTab,
} from '@marketing/insights/insights.interface';
import { Observable, Subject } from 'rxjs';
import { ResponsiveService } from '@frk/eds-components';
import { LocalLinkService } from '@services/local-link.service';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk/';
import { LinkService } from '@services/link.service';
import { AppStateService, SegmentService, SiteConfigService } from '@services';
import { ArticleService } from '@marketing/article/services/article.service';
import { takeUntil } from 'rxjs/operators';

/**
 * The Article Hub component can be used to display hub page for article
 *
 * Selector: `ft-article-hub`
 *
 * Exported as: `ArticleHubComponent`
 */
@Component({
  selector: 'ft-article-hub',
  templateUrl: './article-hub.component.html',
})
export class ArticleHubComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  public page: Page;
  articleHubInfo: ArticleHubContent;
  translationLabels: any;
  isMobile$: Observable<boolean>;
  public relatedStrategies: RelatedStrategies;
  private currentInvestmentTeam: string;

  /**
   * SIM logo resource bundle object
   */
  simLogoResourceBundle: object;
  segmentTags: string;
  /**
   * used to remove subscriptions when component is destroyed
   */
  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * Constructor
   */
  constructor(
    private widenService: WidenService,
    private locallinkService: LocalLinkService,
    private siteConfig: SiteConfigService,
    private linkService: LinkService,
    private appStateService: AppStateService,
    private segmentService: SegmentService,
    private articleService: ArticleService,
    private responsiveService: ResponsiveService
  ) {
    super();
    this.isMobile$ = responsiveService.isMobile$();
    this.segmentService
      .getCurrentSegment$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((segment) => {
        this.segmentTags = segment?.insightAudiences;
      });
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.setBreadcrumbItems();
    this.translationLabels = this.component?.getModels()[
      'ft.core.insightsLibrary'
    ];
    this.simLogoResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos'
    ];
    this.articleHubInfo = this.document?.getData<ArticleHubContent>();

    this.currentInvestmentTeam = this.articleHubInfo?.articleFilters?.investmentteam;

    this.articleHubInfo.summary.value = this.locallinkService.getLocalizeLinks(
      this.articleHubInfo.summary.value
    );
    if (this.articleHubInfo?.logo) {
      this.articleHubInfo.imageSrc = this.widenService.getWidenImageVariantUrl(
        this.widenService.getWidenAssetUrl(
          this.articleHubInfo.logo.widenDocument
        ),
        'original',
        'webp'
      );
    }

    // Related Strategies
    if (this.articleHubInfo?.strategySectionCompound) {
      const rawTabs = this.articleHubInfo?.strategySectionCompound
        .strategyTabSelection;
      this.relatedStrategies = {
        title: this.articleHubInfo.strategySectionCompound.string,
        summary: this.articleHubInfo.strategySectionCompound.summary?.value,
      };
      this.relatedStrategies.tabs = this.getStrategyTabs(rawTabs);
    }
  }

  /**
   * On component destroyed
   * Removes any remaining subscriptions
   */
  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private isVisibleForInvestmentTeam(
    team: string,
    allowedTeam: string
  ): boolean {
    if (allowedTeam) {
      const parsedValue = JSON.parse(allowedTeam);
      const documentInvestmentTeam = parsedValue?.investmentteam_csv
        ? parsedValue?.investmentteam_csv?.split(',')
        : parsedValue;
      const includeInvestmentTeam = documentInvestmentTeam?.filter(
        (investmentTeam) => investmentTeam.includes(team)
      );
      return !!includeInvestmentTeam.length;
    }
  }

  // Strategy Tabs
  private getStrategyTabs(tabs: Array<DocumentRef>): Array<StrategiesTab> {
    const tabsCollection = [];
    tabs?.forEach((tab) => {
      const tabsData = this.page.getContent(tab)?.getData();
      const linkCollection = [];
      tabsData.linkSimTargetingCompound?.forEach((link) => {
        link.localizableLinkCompound?.forEach((linkCompound) => {
          const linkCollectionObj = linkCompound.link.linkCollection[0];

          const isTargeting = linkCompound.targeting;

          const individualLink = {
            ...linkCollectionObj,
            displayText: linkCollectionObj.displayText,
          };

          if (link.openuistring) {
            individualLink.isAllowed = this.isVisibleForInvestmentTeam(
              this.currentInvestmentTeam,
              link.openuistring
            );
          } else if (isTargeting) {
            individualLink.isAllowed = this.articleService.isSegmentTagAvailable(
              this.segmentTags,
              isTargeting
            );
          } else {
            individualLink.isAllowed = true;
          }

          if (linkCollectionObj?.literatureCode) {
            const spaBaseURl = this.appStateService?.apiUrls?.cmsBaseUrl + '/';

            individualLink.url =
              spaBaseURl +
              this.siteConfig.literature.litDetailUrl +
              '/' +
              linkCollectionObj?.literatureCode;
          } else {
            individualLink.url = this.linkService.getCTALink(
              this.page,
              linkCollectionObj,
              null
            );
          }
          linkCollection.push(individualLink);
        });
      });
      tabsData.contentLinks = linkCollection;
      tabsCollection.push(tabsData);
    });
    return tabsCollection;
  }
}
